"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import messages from "@/i18n/messages/en.json";
import type {
  PersonalizationSheetStep,
  ResumeReviewForm,
  UseResumePersonalizationSheetInput,
} from "../../personalization.types";
import {
  EMPTY_REVIEW_FORM,
  toEditableProfile,
  toReviewForm,
} from "../../personalization.utils";
import { isCandidateIdentityConflict, PersonalizationApiError } from "../../services/personalizationClient";

import { useCandidateApplyFit } from "./useCandidateApplyFit";
import { useCandidateResumeUpload } from "./useCandidateResumeUpload";
export function useResumePersonalizationSheet({
  onClose,
  onComplete,
  onParsed,
  closeOnComplete = true,
  controller,
  initialStep,
  uploadSource = "jobs_page",
  job,
  skipFitCheck = false,
}: UseResumePersonalizationSheetInput) {
  const t = useTranslations("profile.resumePersonalization");
  const [step, setStep] = useState<PersonalizationSheetStep>(() => {
    if (initialStep) return initialStep;
    return controller.isSessionLoading
      ? "parsing"
      : controller.isPersonalized
        ? "manage"
        : "upload";
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [hasUploadConsent, setHasUploadConsent] = useState(false);
  const [reviewForm, setReviewForm] = useState<ResumeReviewForm>(() =>
    controller.editableProfile
      ? toReviewForm(controller.editableProfile)
      : EMPTY_REVIEW_FORM,
  );
  const [signInEmail, setSignInEmail] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [code, setCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [fitAttemptId, setFitAttemptId] = useState<string | undefined>();
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const showIdentitySignIn = () => {
    setLocalError(uploadSource === "talent_network"
      ? messages.common.candidatePhoneIdentityConflict
      : messages.common.candidateIdentityConflict);
    setCode("");
    setFitAttemptId(undefined);
    setStep("sign_in");
  };

  const completeApply = useCallback(async () => {
    try {
      await onComplete?.();
      onClose();
    } catch {
      setStep("unavailable");
    }
  }, [onClose, onComplete]);

  const { chooseFile, upload } = useCandidateResumeUpload({
    controller,
    job,
    uploadSource,
    skipFitCheck,
    onParsed,
    completeApply,
    mounted,
    selectedFile,
    hasUploadConsent,
    setSelectedFile,
    setLocalError,
    setStep,
    setReviewForm,
    onIdentityConflict: showIdentitySignIn,
  });
  const checkApplyFit = useCandidateApplyFit({
    controller,
    job,
    skipFitCheck,
    uploadSource,
    initialStep,
    setStep,
    setVerificationEmail,
    setFitAttemptId,
    completeApply,
  });

  const review = async () => {
    const profile = toEditableProfile(reviewForm);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      setLocalError(t("errors.invalidEmail"));
      return;
    }
    if (
      profile.titles.length === 0 &&
      profile.skills.length === 0 &&
      profile.keywords.length === 0
    ) {
      setLocalError(t("errors.missingPreferences"));
      return;
    }
    setLocalError(null);
    setVerificationEmail(profile.email);
    if (uploadSource === "apply_gate") {
      await checkApplyFit(profile);
    } else {
      try {
        const verificationRequired =
          await controller.requestProfileVerification(profile, {
            source: uploadSource,
          });
        if (verificationRequired) {
          setStep("otp");
        } else {
          await onComplete?.();
          if (closeOnComplete) onClose();
        }
      } catch (error) {
        if (isCandidateIdentityConflict(error)) showIdentitySignIn();
        else setStep("review");
      }
    }
  };

  const signIn = async () => {
    const email = signInEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLocalError(t("errors.invalidEmail"));
      return;
    }
    setLocalError(null);
    setVerificationEmail(email);
    setStep("otp");
    try {
      const result = await controller.requestSignIn(email, {
        source: uploadSource,
      });
      if (!result.verificationRequired) {
        if (uploadSource === "apply_gate" && result.editableProfile) {
          setReviewForm(toReviewForm(result.editableProfile));
          setStep("parsing");
          await checkApplyFit(result.editableProfile);
        } else if (uploadSource === "talent_network") {
          if (result.hasResume || result.editableProfile) {
            await onComplete?.();
            if (closeOnComplete) onClose();
          } else setStep("upload");
        } else {
          await onComplete?.();
          onClose();
        }
      }
    } catch (error) {
      if (
        error instanceof PersonalizationApiError &&
        error.code === "CANDIDATE_PROFILE_NOT_FOUND"
      ) {
        setLocalError(t("errors.profileNotFound"));
        setStep("upload");
        return;
      }
      setStep("sign_in");
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) {
      setLocalError(t("errors.invalidCode"));
      return;
    }
    setLocalError(null);
    try {
      const session = await controller.verifyCode(
        verificationEmail,
        code,
        fitAttemptId,
      );
      if (uploadSource === "apply_gate" && skipFitCheck) {
        // Collect-only gate: OTP success is all we needed — redirect.
        await completeApply();
      } else if (uploadSource === "apply_gate" && session.applyGate?.status) {
        if (session.applyGate.status === "match") {
          await completeApply();
        } else {
          setStep(session.applyGate.status);
        }
      } else if (
        uploadSource === "apply_gate" &&
        controller.verificationMode === "login"
      ) {
        if (session.editableProfile) {
          setReviewForm(toReviewForm(session.editableProfile));
          setStep("parsing");
          await checkApplyFit(session.editableProfile);
        } else {
          setStep("upload");
        }
      } else if (
        uploadSource !== "apply_gate" &&
        (session.hasResume || Boolean(session.editableProfile))
      ) {
        await onComplete?.();
        if (closeOnComplete) onClose();
      } else if (uploadSource === "apply_gate") {
        setStep("unavailable");
      } else setStep("upload");
    } catch (error) {
      if (isCandidateIdentityConflict(error)) showIdentitySignIn();
      // The controller exposes the API error in the sheet.
    }
  };

  const resend = async () => {
    setLocalError(null);
    try {
      if (controller.verificationMode === "login") {
        await controller.requestSignIn(verificationEmail, {
          source: uploadSource,
        });
      } else {
        if (uploadSource === "apply_gate") {
          const result = await controller.requestApplyFitVerification(
            toEditableProfile(reviewForm),
            {
              source: uploadSource,
              ...(job ? { job } : {}),
              fitAttemptId,
              ...(skipFitCheck ? { skipFit: true } : {}),
            },
          );
          if (result.applyGate?.fitAttemptId) {
            setFitAttemptId(result.applyGate.fitAttemptId);
          }
        } else {
          await controller.requestProfileVerification(
            toEditableProfile(reviewForm),
            { source: uploadSource },
          );
        }
      }
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  const goBack = () => {
    setLocalError(null);
    setStep(
      step === "otp"
        ? controller.verificationMode === "login"
          ? "sign_in"
          : "review"
        : "upload",
    );
  };

  const showUpload = () => {
    setLocalError(null);
    setSelectedFile(null);
    setHasUploadConsent(false);
    setStep("upload");
  };

  const logout = async () => {
    try {
      await controller.logout();
      setStep("upload");
    } catch {
      // The controller exposes the API error in the sheet.
    }
  };

  const retryFit = () => {
    setFitAttemptId(undefined);
    setStep("review");
  };

  return {
    step,
    selectedFile,
    hasUploadConsent,
    setHasUploadConsent,
    reviewForm,
    signInEmail,
    verificationEmail,
    code,
    error: localError || controller.error,
    showBack: step === "review" || step === "sign_in" || step === "otp",
    chooseFile,
    setReviewForm,
    setSignInEmail,
    setCode,
    setStep,
    upload,
    review,
    signIn,
    verify,
    resend,
    goBack,
    showUpload,
    logout,
    retryFit,
  };
}
